import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type Currency = "USD" | "EUR" | "GBP" | "RUB" | "BRL" | "CNY";

/**
 * Fallback table, used only until the live rates load — or if they never do.
 *
 * These were the hardcoded values the app shipped with. They are kept so a failed
 * fetch degrades to "slightly wrong" instead of "blank price", but they are not the
 * source of truth: /api/config/exchange-rates is.
 */
const FALLBACK_RATES: Record<Currency, number> = {
  USD: 1,
  EUR: 0.92,
  GBP: 0.79,
  RUB: 92,
  BRL: 5.0,
  CNY: 7.23,
};

const SYMBOLS: Record<Currency, string> = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  RUB: "₽",
  BRL: "R$",
  CNY: "¥",
};

type CurrencyContextValue = {
  currency: Currency;
  setCurrency: (c: Currency) => void;
  format: (usd: number) => string;
  /** True while displayed non-USD prices still come from the fallback table. */
  ratesAreEstimated: boolean;
};

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrencyState] = useState<Currency>("USD");
  const [rates, setRates] = useState<Record<Currency, number>>(FALLBACK_RATES);
  const [ratesAreEstimated, setRatesAreEstimated] = useState(true);

  useEffect(() => {
    const stored = (typeof window !== "undefined" &&
      localStorage.getItem("currency")) as Currency | null;
    if (stored && stored in FALLBACK_RATES) setCurrencyState(stored);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/config/exchange-rates");
        if (!res.ok) return; // keep the fallback, and keep flagging it as estimated
        const data = await res.json();
        if (cancelled || !data?.rates) return;
        setRates({ USD: 1, ...data.rates });
        setRatesAreEstimated(Boolean(data.stale));
      } catch {
        // Offline or backend down — the fallback table is still usable.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setCurrency = (c: Currency) => {
    setCurrencyState(c);
    if (typeof window !== "undefined") localStorage.setItem("currency", c);
  };

  const format = (usd: number) => {
    const value = usd * (rates[currency] ?? FALLBACK_RATES[currency]);
    const digits = currency === "RUB" || currency === "CNY" ? 0 : 2;
    return `${SYMBOLS[currency]}${value.toLocaleString(undefined, {
      minimumFractionDigits: digits,
      maximumFractionDigits: 5,
    })}`;
  };

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, format, ratesAreEstimated }}>
      {children}
    </CurrencyContext.Provider>
  );
}

/**
 * A dollar amount, always written in dollars.
 *
 * The referral pitch ("get $1 for every friend who tops up $3") is a fixed, round
 * promise, and the display currency turned it into "получайте 92 ₽ ... 276 ₽" —
 * true, but no longer a slogan. Reward and threshold are denominated in USD and paid
 * out in USDT, so the copy says so; balances keep following the currency picker.
 */
export function formatUsd(usd: number): string {
  const digits = Number.isInteger(usd) ? 0 : 2;
  return `$${usd.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: 2,
  })}`;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency outside provider");
  return ctx;
}

export const CURRENCIES: Currency[] = ["USD", "EUR", "GBP", "RUB", "BRL", "CNY"];
